import { useEffect, useRef, useState } from "react";

import { haptic } from "@/lib/haptics";

/**
 * Native-style pull-to-refresh for page-level scrolling.
 *
 * Starts only when the page is scrolled to the very top and the finger moves
 * down; the returned `pull` (0 → ~1.4 × threshold) drives an indicator.
 * Releasing past `threshold` calls `onRefresh()` and keeps `refreshing` true
 * until it settles.
 *
 *   const { pull, refreshing } = usePullToRefresh(load, { enabled: isMobile });
 */
export default function usePullToRefresh(onRefresh, { enabled = true, threshold = 72 } = {}) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const state = useRef({ startY: 0, startX: 0, active: false, armed: false, pull: 0 });
  const refreshFn = useRef(onRefresh);
  refreshFn.current = onRefresh;
  const busy = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    // We draw our own indicator: stop the browser's built-in reload gesture.
    document.documentElement.classList.add("ptr-lock");

    const onStart = (e) => {
      if (busy.current || e.touches.length !== 1 || window.scrollY > 0) return;
      // Don't hijack gestures that start inside a horizontally/vertically scrollable region or a sheet.
      if (e.target.closest?.("[data-no-ptr], [role='dialog'], [data-vaul-drawer]")) return;
      const t = e.touches[0];
      state.current = { startY: t.clientY, startX: t.clientX, active: true, armed: false, pull: 0 };
    };

    const onMove = (e) => {
      const s = state.current;
      if (!s.active) return;
      const t = e.touches[0];
      const dy = t.clientY - s.startY;
      const dx = t.clientX - s.startX;
      if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) {
        if (s.pull > 0) {
          s.pull = 0;
          setPull(0);
        }
        return;
      }
      if (window.scrollY > 0) {
        s.active = false;
        return;
      }
      // Rubber-band resistance: the further you pull the harder it gets.
      const eased = Math.min(threshold * 1.5, dy * 0.5);
      s.pull = eased;
      setPull(eased);
      const armed = eased >= threshold;
      if (armed !== s.armed) {
        s.armed = armed;
        if (armed) haptic("tick");
      }
    };

    const onEnd = async () => {
      const s = state.current;
      if (!s.active) return;
      s.active = false;
      if (s.armed && !busy.current) {
        busy.current = true;
        setRefreshing(true);
        setPull(threshold * 0.8);
        try {
          await refreshFn.current?.();
        } finally {
          busy.current = false;
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      document.documentElement.classList.remove("ptr-lock");
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled, threshold]);

  return { pull, refreshing, threshold };
}
